import { useEffect, useState, useRef } from 'react';
import { Phone, PhoneOff, User, Video, VideoOff, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface CallingScreenProps {
  recipientName: string;
  recipientRole: 'specialist' | 'caregiver';
  onCancel: () => void;
  onTimeout: () => void;
  timeoutSeconds?: number;
}

export function CallingScreen({
  recipientName,
  recipientRole,
  onCancel,
  onTimeout,
  timeoutSeconds = 60,
}: CallingScreenProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [isCameraEnabled, setIsCameraEnabled] = useState(true);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Get camera stream for preview
  useEffect(() => {
    let stream: MediaStream | null = null;
    
    const getMediaStream = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        setLocalStream(stream);
        setCameraError(null);
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Failed to get media stream:', err);
        setCameraError('Could not access camera. Please check permissions.');
      }
    };
    
    getMediaStream();
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Update video element when stream changes
  useEffect(() => {
    if (videoRef.current && localStream) {
      videoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Toggle camera
  const toggleCamera = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsCameraEnabled(videoTrack.enabled);
      }
    }
  };

  // Toggle microphone
  const toggleMic = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMicEnabled(audioTrack.enabled);
      }
    }
  };

  // Play outgoing call tone
  useEffect(() => {
    const playOutgoingTone = () => {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        
        const playBeep = (startTime: number) => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = 440;
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.1, startTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.5);
          
          oscillator.start(startTime);
          oscillator.stop(startTime + 0.5);
        };

        let currentTime = audioContext.currentTime;
        const scheduleBeeps = () => {
          playBeep(currentTime);
          playBeep(currentTime + 0.6);
          currentTime += 3;
        };

        scheduleBeeps();
        const beepInterval = setInterval(scheduleBeeps, 3000);

        return () => {
          clearInterval(beepInterval);
          audioContext.close();
        };
      } catch (e) {
        console.log('Audio not supported');
        return () => {};
      }
    };

    const cleanup = playOutgoingTone();
    return cleanup;
  }, []);

  // Timer and timeout
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    timeoutRef.current = setTimeout(() => {
      onTimeout();
    }, timeoutSeconds * 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [timeoutSeconds, onTimeout]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const roleLabel = recipientRole === 'specialist' ? 'Sleep Coach' : 'Caregiver';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
      {/* Video Preview Container */}
      <div className="relative w-full h-full max-w-4xl max-h-[80vh] mx-auto">
        {/* Local Video Preview */}
        <div className="relative w-full h-full bg-black rounded-lg overflow-hidden">
          {localStream && isCameraEnabled ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover mirror"
              style={{ transform: 'scaleX(-1)' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <div className="text-center">
                <User className="h-24 w-24 mx-auto text-muted-foreground mb-4" />
                {cameraError ? (
                  <p className="text-sm text-destructive">{cameraError}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">Camera is off</p>
                )}
              </div>
            </div>
          )}

          {/* Overlay with calling info */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 pointer-events-none" />

          {/* Top bar - Calling status */}
          <div className="absolute top-0 left-0 right-0 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Pulsing indicator */}
                <div className="relative">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75" />
                </div>
                <span className="text-white text-sm font-medium">Calling...</span>
              </div>
              <span className="text-white/70 text-sm font-mono">{formatTime(elapsedSeconds)}</span>
            </div>
          </div>

          {/* Center - Recipient info */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
            <div className="relative mx-auto w-20 h-20 mb-4">
              <div className="absolute inset-0 bg-white/20 rounded-full animate-ping" style={{ animationDuration: '1.5s' }} />
              <div className="absolute inset-2 bg-white/30 rounded-full animate-ping" style={{ animationDuration: '1.5s', animationDelay: '0.3s' }} />
              <div className="relative w-full h-full bg-white/10 backdrop-blur rounded-full flex items-center justify-center border-2 border-white/30">
                <User className="h-10 w-10 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-1">{recipientName}</h2>
            <p className="text-white/70 text-sm">{roleLabel}</p>
          </div>

          {/* Bottom bar - Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
            <div className="flex items-center justify-center gap-4">
              {/* Mic toggle */}
              <Button
                variant="outline"
                size="lg"
                className={`rounded-full w-14 h-14 ${!isMicEnabled ? 'bg-destructive/20 border-destructive text-destructive' : 'bg-white/10 border-white/20 text-white hover:bg-white/20'}`}
                onClick={toggleMic}
              >
                {isMicEnabled ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
              </Button>

              {/* Cancel call */}
              <Button
                variant="destructive"
                size="lg"
                className="rounded-full w-16 h-16"
                onClick={onCancel}
              >
                <PhoneOff className="h-7 w-7" />
              </Button>

              {/* Camera toggle */}
              <Button
                variant="outline"
                size="lg"
                className={`rounded-full w-14 h-14 ${!isCameraEnabled ? 'bg-destructive/20 border-destructive text-destructive' : 'bg-white/10 border-white/20 text-white hover:bg-white/20'}`}
                onClick={toggleCamera}
              >
                {isCameraEnabled ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
              </Button>
            </div>
            <p className="text-center text-white/50 text-sm mt-3">Waiting for {recipientName} to answer...</p>
          </div>
        </div>
      </div>
    </div>
  );
}
